// Implements: F-USG-006
/**
 * The per-version breakdown of one mockup in Mi consumo (mockup v3 P7, v3 `verBreakdown`): the
 * mockup's newest failed or cancelled turns first ("Turno fallido", why under «Qué cambió», "No se
 * cobró" when it was not charged), then its eight newest versions with when they were made, what
 * changed and how long their run took, then how many older versions are left out. Pure
 * presentation: the container reads the data.
 *
 * Owner 2026-10-19: every row shows two costs, "Mi consumo" (only the caller's own runs: a
 * co-editor's run reads "USD 0.00" there) and "Consumo total" (the whole mockup), and the table
 * closes with the mockup's totals, the caller's share and everybody's, one row per currency (money
 * is never added across currencies, OD-12). A version no run produced is "Sin coste" in both. The
 * caption names both, so neither is read as the other. The totals span the mockup's whole life
 * (older versions and text answers included), and the footer says so.
 *
 * A run the server has not counted yet (the reporting projection runs after the run closed) reads
 * "Pendiente" under Mi consumo, never "USD 0.00" beside a total that shows its cost.
 *
 * Owner 2026-10-19 (answer 1A): «Qué cambió», after «Cuándo» as v3 draws it, is the person's request
 * that produced the version, as the server cut it (60 characters plus «…»); the first version reads
 * «Primera versión» (v3 `finishTurn`). A request whose author's account was erased reads «Mensaje
 * eliminado» (owner 2026-10-19), never the stored placeholder; a version no person's turn produced,
 * or one whose costs are not loaded, reads «—».
 */

import type { ReactElement } from "react";
import { Link } from "react-router";

import { useTranslation } from "../../../shared/i18n";
import { costAmountText, durationText } from "../../../shared/ui/costText";
import { dayText } from "../../../shared/ui/dayText";
import type { CostSummary, VersionTreeNode } from "../../../types/api";
import type { FailedRun, FailureCode, SpendTotal, VersionCostItem } from "../../../types/costs";
import styles from "./MyUsage.module.css";

const NO_VALUE = "—";
/** The cells a totals row's label spans: version, when, what changed and duration. */
const LABEL_SPAN = 4;
/** The sequence number of a mockup's first version: «Primera versión». */
const FIRST_SEQUENCE = 1;

export interface VersionCostBreakdownProps {
  readonly mockupId: string;
  readonly name: string;
  /** The versions to list, newest first. */
  readonly versions: readonly VersionTreeNode[];
  /** How many older versions are left out. */
  readonly older: number;
  readonly costs: ReadonlyMap<string, VersionCostItem>;
  readonly failedRuns: readonly FailedRun[];
  /** The generation ids among the listed runs that are the caller's own. */
  readonly ownRuns: ReadonlySet<string>;
  /** The generation ids among the listed runs the server has counted yet. */
  readonly knownRuns: ReadonlySet<string>;
  /** The mockup's totals per currency: the caller's share and everybody's. */
  readonly totals: readonly SpendTotal[];
}

/**
 * Why a failed turn produced no version. A cancelled run says so; a failed one says why by the
 * closed token its record stores (owner 2026-10-19, migration 0041: a timeout, a provider outage).
 * `internal`, `null` (a run that failed before the record kept why) and any token this client does
 * not know read the generic "No se completó": never the raw token (CL 10). Dictionary keys are
 * camelCase (AD-05), so each token names its key here; the map is total over the known tokens.
 */
type ReasonKey = `cost.breakdown.reason.${
  | "cancelled"
  | "incomplete"
  | "timeout"
  | "providerUnavailable"
  | "providerRejected"
  | "contextExceeded"
  | "outOfCredits"
  | "outputTruncated"
  | "invalidOutput"}`;
const FAILURE_REASON: Readonly<Record<Exclude<FailureCode, "internal">, ReasonKey>> = {
  timeout: "cost.breakdown.reason.timeout",
  provider_unavailable: "cost.breakdown.reason.providerUnavailable",
  provider_rejected: "cost.breakdown.reason.providerRejected",
  context_exceeded: "cost.breakdown.reason.contextExceeded",
  out_of_credits: "cost.breakdown.reason.outOfCredits",
  output_truncated: "cost.breakdown.reason.outputTruncated",
  invalid_output: "cost.breakdown.reason.invalidOutput",
};
const REASON_CANCELLED: ReasonKey = "cost.breakdown.reason.cancelled";
const REASON_UNKNOWN: ReasonKey = "cost.breakdown.reason.incomplete";

/** Own keys only: a code such as `toString` must not read the object's prototype. */
function reasonOf(run: FailedRun): ReasonKey {
  if (run.status === "cancelled") return REASON_CANCELLED;
  const code = run.failure_code;
  const known: Readonly<Partial<Record<string, ReasonKey>>> = FAILURE_REASON;
  return (code !== null && Object.hasOwn(known, code) ? known[code] : undefined) ?? REASON_UNKNOWN;
}

/** What the caller spent of `cost`: all of it on their own run, nothing on somebody else's. */
/**
 * The «Qué cambió» text of a version's request: «—» when there is none, including an empty one (a
 * request with nothing a person could read is no request, verifier fix, lane 50).
 */
function requestText(excerpt: string | null | undefined): string {
  if (excerpt === undefined || excerpt === null || excerpt.length === 0) return NO_VALUE;
  return excerpt;
}

function mineOf(cost: CostSummary, own: boolean): string {
  return costAmountText(own ? cost : { amount_micros: 0, currency: cost.currency });
}

export function VersionCostBreakdown({
  mockupId,
  name,
  versions,
  older,
  costs,
  failedRuns,
  ownRuns,
  knownRuns,
  totals,
}: VersionCostBreakdownProps): ReactElement {
  const { t } = useTranslation();
  /** The caller's share of one run, or "Pendiente" while the server has not counted it. */
  const share = (generationId: string, cost: CostSummary): string =>
    knownRuns.has(generationId) ? mineOf(cost, ownRuns.has(generationId)) : t("cost.breakdown.pending");
  return (
    <div className={styles.breakdown}>
      <table className={styles.table}>
        <caption className={styles.caption}>{t("cost.breakdown.title", { name })}</caption>
        <thead>
          <tr>
            <th scope="col">{t("cost.breakdown.version")}</th>
            <th scope="col">{t("cost.breakdown.when")}</th>
            <th scope="col">{t("cost.breakdown.what")}</th>
            <th scope="col">{t("cost.breakdown.duration")}</th>
            <th scope="col">{t("cost.breakdown.mine")}</th>
            <th scope="col">{t("cost.breakdown.total")}</th>
          </tr>
        </thead>
        <tbody>
          {failedRuns.map((run) => (
            <tr key={run.generation_id} className={styles.failedTurn}>
              <th scope="row">{t("cost.breakdown.failedTurn")}</th>
              <td>
                <time dateTime={run.started_at}>{dayText(run.started_at)}</time>
              </td>
              <td>{t(reasonOf(run))}</td>
              <td>{NO_VALUE}</td>
              <td>{run.chargeable ? share(run.generation_id, run.cost) : t("cost.notCharged")}</td>
              <td>{run.chargeable ? costAmountText(run.cost) : t("cost.notCharged")}</td>
            </tr>
          ))}
          {versions.map((version) => {
            const item = costs.get(version.id);
            const run = item?.run;
            const none = item === undefined ? NO_VALUE : t("cost.none");
            const what =
              version.sequence_number === FIRST_SEQUENCE
                ? t("cost.breakdown.firstVersion")
                : item?.request_erased === true
                  ? t("cost.breakdown.requestErased")
                  : requestText(item?.request_excerpt);
            return (
              <tr key={version.id}>
                <th scope="row">
                  <Link className={styles.link} to={`/lienzo/${encodeURIComponent(mockupId)}`}>
                    {t("editor.versions.chip", { n: version.sequence_number })}
                  </Link>
                </th>
                <td>
                  <time dateTime={version.created_at}>{dayText(version.created_at)}</time>
                </td>
                <td>{what}</td>
                <td>{run ? durationText(run.duration_ms) : NO_VALUE}</td>
                <td>{run ? share(run.generation_id, run.cost) : none}</td>
                <td>{run ? costAmountText(run.cost) : none}</td>
              </tr>
            );
          })}
        </tbody>
        {totals.length > 0 ? (
          <tfoot>
            {totals.map((total) => (
              <tr key={total.currency}>
                <th scope="row" colSpan={LABEL_SPAN}>
                  {t("cost.breakdown.mockupTotal")}
                </th>
                <td>{costAmountText({ amount_micros: total.mine_micros, currency: total.currency })}</td>
                <td>{costAmountText({ amount_micros: total.all_micros, currency: total.currency })}</td>
              </tr>
            ))}
          </tfoot>
        ) : null}
      </table>
      {older > 0 ? <p className={styles.hint}>{t("cost.breakdown.older", { count: older })}</p> : null}
    </div>
  );
}
